import React, { useRef, useState } from 'react';

import {
  PAYMENT_MODE_LABELS,
  PAYMENT_MODE_META,
  allowedRefundModes,
  CASH_MODE,
  RETURN_ADVANCE_REASON,
} from '../../../constants/admission';
import { formatPaise } from '../../../utils/money';
import { newIdempotencyKey } from '../../../services/api/admissionPaymentsApi';
import { describeBalance } from './billModel';

const overlayCls = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4';
const panelCls = 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl w-full max-w-md max-h-[92vh] overflow-y-auto';
const btnPrimary = 'px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60';
const btnSecondary = 'px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm';
const inputCls = 'w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm';
const labelCls = 'block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';
const modeChipOn = 'px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border bg-teal-600 border-teal-600 text-white';
const modeChipOff = 'px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700';

/**
 * ReturnAdvanceDialog (owner redesign — the BOUNDED discharge-time return) —
 * returns EXACTLY the pending excess advance. Perm RETURN_ADVANCE (reception +
 * admin). Distinct from the free-form RefundDialog (admin-only): here the amount
 * is READ-ONLY (server sets it = the definite excess) and the reason is a preset
 * (server sets the real reason — the client sends NONE). The only choice the
 * user makes is the payout METHOD (Cash / UPI / Card, default Cash); UPI/Card
 * reveal a REQUIRED reference input. Submit calls the NEW return-advance endpoint
 * (method + reference + idempotency), never the free-form refund. In-app modal.
 *
 * @param amountPaise   the pending excess to return (display-only; the SERVER is authoritative)
 * @param balancePaise  server-cached signed balance at open (for the after-return preview)
 * @param onSubmit(body) → { success, data?, error?, code? } — body = { method, reference?, idempotency_key }
 * @param onPrintVoucher(paymentId, {isRefund:true}) → opens the voucher PDF
 */
export default function ReturnAdvanceDialog({ admission, parent, amountPaise, balancePaise, onClose, onSubmit, onPrintVoucher }) {
  const idemKey = useRef(newIdempotencyKey('return-advance'));
  const [method, setMethod] = useState(CASH_MODE);
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const modes = allowedRefundModes();
  const meta = PAYMENT_MODE_META[method] || {};
  const needsReference = !!meta.reference;
  const referenceValid = !needsReference || reference.trim().length > 0;
  const returnPaise = Math.max(0, Math.round(Number(amountPaise) || 0));
  const valid = returnPaise > 0 && referenceValid && modes.includes(method);

  // Returning the advance lifts the balance toward zero: after = owed + returned.
  const afterBal = describeBalance((Math.round(Number(balancePaise) || 0)) + returnPaise);

  const pickMethod = (m) => {
    setMethod(m);
    if (!PAYMENT_MODE_META[m]?.reference) setReference('');
  };

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    const res = await onSubmit({
      method,
      ...(needsReference ? { reference: reference.trim() } : {}),
      idempotency_key: idemKey.current,
    });
    setBusy(false);
    if (res && res.success) {
      setDone(res.data);
      return;
    }
    if (res) {
      setError(
        res.code === 'REFERENCE_REQUIRED' ? 'A reference number is required for a UPI or card payout.'
          : res.code === 'NO_EXCESS_TO_RETURN' ? 'There’s no unused advance to return — the balance has already settled.'
            : res.code === 'ADMISSION_NOT_ACTIVE' ? 'The advance can only be returned while the patient is admitted. After discharge, money movement happens at final settlement.'
              : res.error || 'Could not return the advance. Try again.',
      );
    }
  };

  // ── Success: voucher hand-off ────────────────────────────────────────────
  if (done) {
    const refund = done.refund || {};
    const settled = describeBalance(done.balance_paise_after);
    return (
      <div className={overlayCls} data-testid="return-advance-dialog">
        <div className={panelCls}>
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Advance returned</h3>
            <button type="button" onClick={onClose} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
          </div>
          <div className="p-4 space-y-3" data-testid="return-advance-success">
            <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-[13px] p-3">
              Voucher <b className="tabular-nums">{refund.receipt_number || '—'}</b> — hand the printed copy to the patient.
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2.5 text-[13px] space-y-1">
              <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Advance returned</span><b className="tabular-nums text-slate-900 dark:text-white">{formatPaise(refund.amount_paise)}</b></div>
              <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">{settled.label} after return</span><b className="tabular-nums text-slate-900 dark:text-white">{settled.text}</b></div>
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Return of an advance deposit — not a tax document.</p>
          </div>
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
            <button type="button" className={btnSecondary} onClick={onClose} data-testid="return-advance-done">Done</button>
            {onPrintVoucher && refund.payment_id && (
              <button type="button" className={btnPrimary} onClick={() => onPrintVoucher(refund.payment_id, { isRefund: true })} data-testid="return-advance-print">Print voucher</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={overlayCls} data-testid="return-advance-dialog">
      <div className={panelCls}>
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Return advance</h3>
          <button type="button" onClick={onClose} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
        </div>
        <div className="p-4 space-y-3">
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-[12.5px] text-slate-600 dark:text-slate-300 px-3 py-2">
            Deposit <b className="text-slate-900 dark:text-white tabular-nums">{parent.receipt_number || '—'}</b> · {PAYMENT_MODE_LABELS[parent.method] || parent.method} · <b className="text-slate-900 dark:text-white">{admission.patient_name || admission.patient_id}</b>
          </div>

          {error && (
            <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="return-advance-error">{error}</div>
          )}

          {/* Amount — READ-ONLY (server sets it = the definite excess). */}
          <div className="flex items-center justify-between rounded-lg border border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 px-3 py-2.5">
            <span className="text-[13px] text-teal-800 dark:text-teal-200">Advance to return</span>
            <b className="text-[15px] tabular-nums text-teal-800 dark:text-teal-200" data-testid="return-advance-amount">{formatPaise(returnPaise)}</b>
          </div>

          {/* Reason — preset, read-only (server sets the real reason; client sends none). */}
          <div>
            <label className={labelCls}>Reason</label>
            <div className="text-[13px] text-slate-800 dark:text-slate-100 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2" data-testid="return-advance-reason">{RETURN_ADVANCE_REASON}</div>
          </div>

          <div>
            <label className={labelCls}>Return by</label>
            <div className="flex gap-1.5 flex-wrap" role="group" aria-label="Return mode">
              {modes.map((m) => (
                <button key={m} type="button" onClick={() => pickMethod(m)} className={method === m ? modeChipOn : modeChipOff} data-testid={`return-advance-mode-${m}`}>
                  {PAYMENT_MODE_LABELS[m] || m}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1" data-testid="return-advance-hint">
              Returning the patient’s unused advance. UPI and card payouts need a reference number.
            </p>
            {needsReference && (
              <div className="mt-2">
                <label className={labelCls} htmlFor="ra-reference">{meta.refLabel || 'Reference number'} <span className="text-red-500">*</span></label>
                <input id="ra-reference" className={inputCls} type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder={meta.refLabel || 'Reference number'} data-testid="return-advance-reference" />
              </div>
            )}
          </div>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2.5">
            <span className="text-[13px] text-slate-600 dark:text-slate-300">{afterBal.label} after this return</span>
            <b className="text-[15px] tabular-nums text-slate-900 dark:text-white" data-testid="return-advance-after">{afterBal.text}</b>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onClose}>Cancel</button>
          <button type="button" className={btnPrimary} onClick={submit} disabled={!valid || busy} data-testid="return-advance-confirm">
            {busy ? 'Returning…' : 'Return advance & print voucher'}
          </button>
        </div>
      </div>
    </div>
  );
}
